import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaSearch,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "../lib/axios";
import { useAuth } from "../authContext";
import { toast } from "react-toastify";

const PAGE_SIZE = 6;
const DISPLAY_SERIF =
  "Iowan Old Style, Palatino Linotype, Book Antiqua, Georgia, serif";

const formatDate = (dateString) => {
  if (!dateString) return "NA";

  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value = 0) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

const getQuantityEntries = (quantity) => {
  if (!quantity) return [];

  const parsedQuantity =
    typeof quantity === "string"
      ? (() => {
          try {
            return JSON.parse(quantity);
          } catch (error) {
            return quantity;
          }
        })()
      : quantity;

  if (typeof parsedQuantity === "object" && !Array.isArray(parsedQuantity)) {
    return Object.entries(parsedQuantity);
  }

  return [["Quantity", parsedQuantity]];
};

const getItemSupportingText = (item) => {
  if (item?.description) {
    return item.description;
  }

  return getQuantityEntries(item?.quantity)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([unit, value]) => `${unit}: ${value}`)
    .join(" • ");
};

const getQuotationSummary = (quotation) => {
  const items = quotation?.cart_items || [];

  if (items.length === 0) {
    return "No line items";
  }

  const firstItem = items[0];
  const firstLine = [firstItem?.name, getItemSupportingText(firstItem)]
    .filter(Boolean)
    .join(" • ");

  if (items.length === 1) {
    return firstLine || "1 item";
  }

  return `${firstLine || `${items.length} items`} + ${items.length - 1} more`;
};

const matchesQuotationSearch = (quotation, rawSearch) => {
  const searchTerm = rawSearch.trim().toLowerCase();

  if (!searchTerm) return true;

  const searchableValues = [
    quotation.quotation_number,
    quotation.billing_details?.company_name,
    quotation.billing_details?.name,
    quotation.billing_details?.city,
    quotation.billing_details?.email,
    quotation.billing_details?.phone,
    quotation.billing_details?.gst_number,
    getQuotationSummary(quotation),
    ...(quotation.cart_items || []).flatMap((item) => [
      item?.name,
      item?.description,
      ...getQuantityEntries(item?.quantity).map(
        ([unit, value]) => `${unit} ${value}`,
      ),
    ]),
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return searchableValues.some((value) => value.includes(searchTerm));
};

const getPaginationRange = (totalPages, currentPage) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage, "ellipsis", totalPages];
};

const getStatusTone = (quotation) => {
  if (quotation?.billing_details?.gst_number) {
    return "bg-emerald-50 text-emerald-700";
  }

  return "bg-stone-100 text-stone-700";
};

const EmptyState = ({ title, body, actionLabel, onAction }) => (
  <div className="rounded-[22px] border border-dashed border-stone-300 bg-stone-50/70 px-4 py-10 text-center sm:px-5">
    <h2
      className="text-2xl text-stone-900"
      style={{ fontFamily: DISPLAY_SERIF }}
    >
      {title}
    </h2>
    <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-stone-500">
      {body}
    </p>
    {actionLabel && onAction ? (
      <button
        type="button"
        onClick={onAction}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-stone-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-stone-800"
      >
        {actionLabel}
        <FaArrowRight className="text-[11px]" />
      </button>
    ) : null}
  </div>
);

const MyOrders = () => {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingQuotationID, setDownloadingQuotationID] = useState(null);

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const response = await axios.get("/api/quotation", {
          params: {
            where: {
              user: {
                equals: user.id,
              },
            },
            limit: 100,
            sort: "-created_at",
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        setQuotations(response.data.docs || []);
      } catch (error) {
        toast.error("Failed to fetch quotations");
        console.error("Error fetching quotations:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchQuotations();
      return;
    }

    setLoading(false);
  }, [user, accessToken]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDownloadQuotation = async (quotation) => {
    try {
      setDownloadingQuotationID(quotation.id);

      const response = await axios.get(`/api/quotation/${quotation.id}/download`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectURL = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectURL;
      link.download = `Quotation-${quotation.quotation_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectURL);
    } catch (error) {
      toast.error("Failed to download quotation PDF");
      console.error("Error downloading quotation PDF:", error);
    } finally {
      setDownloadingQuotationID(null);
    }
  };

  const filteredQuotations = useMemo(
    () =>
      quotations.filter((quotation) =>
        matchesQuotationSearch(quotation, searchTerm),
      ),
    [quotations, searchTerm],
  );

  const totalPages = Math.max(1, Math.ceil(filteredQuotations.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleQuotations = filteredQuotations.slice(
    startIndex,
    startIndex + PAGE_SIZE,
  );
  const pageRange = getPaginationRange(totalPages, currentPage);
  const showingFrom = filteredQuotations.length === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + PAGE_SIZE, filteredQuotations.length);
  const totalValue = filteredQuotations.reduce(
    (sum, quotation) => sum + Number(quotation.total_amount || 0),
    0,
  );

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex min-h-screen items-center justify-center bg-[#f4efe6]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-stone-200 border-t-stone-900"></div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#f4efe6] py-5 sm:py-6">
        <div className="mx-auto max-w-6xl px-4 sm:px-5">
          <section className="overflow-hidden rounded-[26px] border border-stone-200/80 bg-white shadow-[0_16px_50px_rgba(28,25,23,0.06)]">
            <div className="border-b border-stone-200/80 bg-[linear-gradient(135deg,#f8f3eb_0%,#ffffff_56%,#f5ede2_100%)] px-4 py-4 sm:px-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-400">
                    Quotations
                  </p>
                  <h1
                    className="mt-1 text-[28px] leading-none text-stone-900 sm:text-[34px]"
                    style={{ fontFamily: DISPLAY_SERIF }}
                  >
                    My Orders
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-stone-500">
                    Compact view of your quotation history and downloads.
                  </p>
                </div>

                {quotations.length > 0 ? (
                  <label
                    htmlFor="quotation-search"
                    className="flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white px-3 py-2.5 text-stone-500 lg:max-w-sm"
                  >
                    <FaSearch className="text-sm text-stone-400" />
                    <input
                      id="quotation-search"
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search quotation, item, company..."
                      className="w-full bg-transparent text-sm text-stone-800 outline-none placeholder:text-stone-400"
                    />
                  </label>
                ) : null}
              </div>

              {quotations.length > 0 ? (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <div className="rounded-2xl border border-stone-200 bg-white/85 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                      Records
                    </p>
                    <p className="mt-1 text-base font-semibold text-stone-900">
                      {filteredQuotations.length}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-stone-200 bg-white/85 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                      Showing
                    </p>
                    <p className="mt-1 text-base font-semibold text-stone-900">
                      {showingFrom}-{showingTo}
                    </p>
                  </div>
                  <div className="col-span-2 rounded-2xl border border-stone-200 bg-stone-900 px-3 py-2 text-white sm:col-span-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
                      Total Value
                    </p>
                    <p className="mt-1 text-base font-semibold">
                      ₹{formatCurrency(totalValue)}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="px-3 py-3 sm:px-4 sm:py-4">
              {quotations.length === 0 ? (
                <EmptyState
                  title="No quotations yet"
                  body="Your quotation requests will appear here once you create them."
                  actionLabel="Start Exploring"
                  onAction={() => navigate("/advertising")}
                />
              ) : filteredQuotations.length === 0 ? (
                <EmptyState
                  title="No matching results"
                  body="Try a different quotation number, company name, GST number, or item name."
                />
              ) : (
                <>
                  <div className="space-y-3">
                    {visibleQuotations.map((quotation) => {
                      const itemCount = Number(quotation?.cart_items?.length || 0);

                      return (
                        <article
                          key={quotation.id}
                          className="rounded-[20px] border border-stone-200 bg-white p-3 shadow-[0_10px_28px_rgba(28,25,23,0.04)]"
                        >
                          <div className="flex flex-col gap-3 border-b border-stone-200/80 pb-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p
                                  className="text-[26px] leading-none text-stone-900 sm:text-[30px]"
                                  style={{ fontFamily: DISPLAY_SERIF }}
                                >
                                  #{quotation.quotation_number}
                                </p>
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getStatusTone(quotation)}`}
                                >
                                  {quotation.billing_details?.gst_number ? "GST" : "Standard"}
                                </span>
                              </div>
                              <p className="mt-2 text-sm font-semibold text-stone-900">
                                {quotation.billing_details?.company_name ||
                                  quotation.billing_details?.name ||
                                  "Untitled"}
                              </p>
                              <p className="mt-1 line-clamp-2 text-sm leading-6 text-stone-500">
                                {getQuotationSummary(quotation)}
                              </p>
                            </div>

                            <div className="grid grid-cols-3 gap-2 sm:min-w-[280px]">
                              <div className="rounded-2xl bg-stone-50 px-3 py-2">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                  Date
                                </p>
                                <p className="mt-1 text-sm font-semibold text-stone-900">
                                  {formatDate(quotation.created_at)}
                                </p>
                              </div>
                              <div className="rounded-2xl bg-stone-50 px-3 py-2">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                  Items
                                </p>
                                <p className="mt-1 text-sm font-semibold text-stone-900">
                                  {itemCount}
                                </p>
                              </div>
                              <div className="rounded-2xl bg-stone-900 px-3 py-2 text-white">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
                                  Value
                                </p>
                                <p className="mt-1 text-sm font-semibold">
                                  ₹{formatCurrency(quotation.total_amount || 0)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 text-xs uppercase tracking-[0.18em] text-stone-400">
                              {quotation.billing_details?.name ? (
                                <span>{quotation.billing_details.name}</span>
                              ) : (
                                <span>Quotation Record</span>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => handleDownloadQuotation(quotation)}
                              disabled={downloadingQuotationID === quotation.id}
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-stone-700 transition hover:border-stone-300 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaDownload className="text-[11px]" />
                              {downloadingQuotationID === quotation.id
                                ? "Preparing"
                                : "Download PDF"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {totalPages > 1 ? (
                    <section className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400">
                        Page {currentPage} of {totalPages}
                      </p>

                      <div className="flex items-center gap-2 self-start sm:self-auto">
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) => Math.max(1, page - 1))
                          }
                          disabled={currentPage === 1}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 transition hover:border-stone-300 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Previous page"
                        >
                          <FaChevronLeft className="text-[11px]" />
                        </button>

                        {pageRange.map((entry, index) =>
                          entry === "ellipsis" ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="inline-flex h-9 w-9 items-center justify-center text-sm text-stone-400"
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={entry}
                              type="button"
                              onClick={() => setCurrentPage(entry)}
                              className={`inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full border px-3 text-sm transition ${
                                currentPage === entry
                                  ? "border-stone-900 bg-stone-900 text-white"
                                  : "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900"
                              }`}
                            >
                              {entry}
                            </button>
                          ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) => Math.min(totalPages, page + 1))
                          }
                          disabled={currentPage === totalPages}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 transition hover:border-stone-300 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Next page"
                        >
                          <FaChevronRight className="text-[11px]" />
                        </button>
                      </div>
                    </section>
                  ) : null}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyOrders;
